import { useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Client-side image picker for cleaner profile photos.
 * - Downsamples to max 800px wide
 * - Converts to JPEG at ~0.82 quality
 * - Uploads via PUT /api/cleaners/profile/image
 * - Calls onChange(newImageDataUrl) after successful upload
 */
const MAX_W = 800;
const TARGET_QUALITY = 0.82;

async function downscale(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Invalid image'));
      img.onload = () => {
        const scale = Math.min(1, MAX_W / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', TARGET_QUALITY));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function ProfilePhotoUploader({ currentImage, onChange, dataTestId }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [preview, setPreview] = useState(currentImage || null);

  const pick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      setErr('Please choose a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setErr('Image too large — keep it under 8MB.');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await downscale(file);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cleaners/profile/image`, { image_data: dataUrl }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(dataUrl);
      onChange?.(dataUrl);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-5" data-testid={dataTestId || 'profile-photo-uploader'}>
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-dashed border-border bg-surface-muted flex items-center justify-center hover:border-primary transition group disabled:opacity-60"
        data-testid="profile-photo-trigger"
      >
        {preview ? (
          <img src={preview} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <Camera className="w-7 h-7 text-text-secondary group-hover:text-primary transition" />
        )}
        {busy && (
          <span className="absolute inset-0 flex items-center justify-center bg-white/70">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </span>
        )}
      </button>
      <div className="flex-1">
        <p className="font-semibold text-text-primary mb-0.5">Profile photo</p>
        <p className="text-xs text-text-secondary mb-2">
          Required. A clear face photo helps customers trust and book you.
        </p>
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="text-sm text-primary font-semibold hover:underline disabled:opacity-60"
        >
          {preview ? 'Change photo' : 'Choose a photo'}
        </button>
        {err && (
          <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {err}
          </p>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFile}
        data-testid="profile-photo-input"
      />
    </div>
  );
}
